import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Resources', to: '/resources' },
  { label: 'Prayer Requests', to: '/prayer-request' },
];

const getPageTitle = (pathname) => {
  if (pathname === '/') return null; // homepage uses scroll animation
  if (pathname.startsWith('/events')) return 'Events';
  if (pathname.startsWith('/reflections')) return 'Reflections';
  if (pathname === '/resources') return 'Resources';
  if (pathname.startsWith('/prayers')) return 'Prayers';
  if (pathname.startsWith('/prayer-request')) return 'Prayer Requests';
  if (pathname === '/login') return 'Login';
  if (pathname.startsWith('/admin')) return 'Admin';
  return null;
};

const NavMenu = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { pathname } = useLocation();
  const scrollTitleRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const el = scrollTitleRef.current;
    if (!el) return;

    const mql = window.matchMedia('(max-width: 640px)');
    if (!mql.matches) {
      el.style.top = '200px';
      return;
    }

    const navHeight = 56;
    const navFontSize = 1.3;
    const navLetterSpacing = 0.06;

    const pageTitle = getPageTitle(pathname);

    // Non-homepage: always show "Southie Totus Tuus" statically in the nav
    if (pageTitle) {
      el.textContent = 'Southie Totus Tuus';
      el.style.top = `${navHeight / 2}px`;
      el.style.fontSize = `${navFontSize}rem`;
      el.style.letterSpacing = `${navLetterSpacing}em`;
      return;
    }

    // Homepage: animate in sync with the hero title
    el.textContent = 'Southie Totus Tuus';
    el.style.top = '200px';

    const heroFontSize = 2;
    const heroLetterSpacing = 0.02;

    let scrollCleanup = null;

    const attach = (heroTitle) => {
      const onScroll = () => {
        if (!scrollTitleRef.current) return;
        const heroRect = heroTitle.getBoundingClientRect();
        const heroCenter = heroRect.top + heroRect.height / 2;
        const progress = Math.max(0, Math.min(1, (navHeight - heroCenter) / (navHeight / 2)));
        scrollTitleRef.current.style.top           = `${Math.max(navHeight / 2, heroCenter)}px`;
        scrollTitleRef.current.style.fontSize      = `${heroFontSize + (navFontSize - heroFontSize) * progress}rem`;
        scrollTitleRef.current.style.letterSpacing = `${heroLetterSpacing + (navLetterSpacing - heroLetterSpacing) * progress}em`;
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return () => window.removeEventListener('scroll', onScroll);
    };

    const heroTitle = document.querySelector('.stt-hero-title');
    if (heroTitle) {
      scrollCleanup = attach(heroTitle);
    } else {
      const observer = new MutationObserver(() => {
        const found = document.querySelector('.stt-hero-title');
        if (found) {
          observer.disconnect();
          scrollCleanup = attach(found);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      return () => {
        observer.disconnect();
        if (scrollCleanup) scrollCleanup();
      };
    }

    return () => { if (scrollCleanup) scrollCleanup(); };
  }, [pathname]);

  const allLinks = [
    ...NAV_LINKS,
    ...(isLoggedIn ? [{ label: 'Admin', to: '/admin' }] : []),
  ];

  const isActive = (to) => {
    if (to === '/admin') return pathname.startsWith('/admin');
    if (to === '/prayer-request') return pathname.startsWith('/prayer-request');
    return pathname === to;
  };

  return (
    <nav className="stt-nav">

      {/* White identity row */}
      <div className="stt-nav-identity">
        <div className="stt-nav-inner">

          <Link to="/" className="stt-nav-brand" onClick={() => setMenuOpen(false)}>
            <img src={require('../assets/SouthieTotusTuus.png')} alt="Southie Totus Tuus" className="stt-nav-logo" />
          </Link>

          {/* Desktop: title + subtitle centered */}
          <Link to="/" className="stt-nav-brand-text" style={{ textDecoration: 'none' }}>
            <span className="stt-nav-site-title">Southie Totus Tuus</span>
            <span className="stt-nav-site-subtitle">A Gate of Heaven and St. Brigid Young Adults Community</span>
          </Link>

          {/* Mobile: scroll-synced on homepage, static page title elsewhere */}
          <Link ref={scrollTitleRef} to="/" className="stt-nav-scroll-title" onClick={() => setMenuOpen(false)}>
            Southie Totus Tuus
          </Link>

          <div className="stt-nav-right">
            <button
              className="stt-nav-hamburger"
              onClick={() => setMenuOpen(prev => !prev)}
              aria-label="Toggle menu"
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>

        </div>
      </div>

      {/* Links bar (desktop only) */}
      <div className="stt-nav-bar">
        <ul className="stt-nav-links">
          {allLinks.map(link => (
            <li key={link.to}>
              <Link
                to={link.to}
                style={{
                  color: isActive(link.to) ? 'var(--navy-light)' : undefined,
                  fontWeight: isActive(link.to) ? '700' : undefined,
                  textDecoration: isActive(link.to) ? 'underline' : undefined,
                }}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="stt-nav-mobile-menu">
          {allLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              style={{
                color: isActive(link.to) ? 'var(--navy-light)' : undefined,
                fontWeight: isActive(link.to) ? '700' : undefined,
                textDecoration: isActive(link.to) ? 'underline' : undefined,
              }}
            >
              {link.label}
            </Link>
          ))}
          {!isLoggedIn && (
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '4px' }}
            >
              Login
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default NavMenu;
